/* ==========================================================================
   Kitsters money formatting — ONE implementation, driven by the shop
   --------------------------------------------------------------------------
   Nine separate functions across eight scripts used to do this, and every one
   of them hardcoded `'₹' + n.toLocaleString('en-IN')`. That is not a copy
   problem, it is a correctness one: a hardcoded symbol ignores the store's own
   currency, so the same figure that renders "$12.99" from Liquid rendered
   "₹13" the moment JavaScript touched it. Nine copies of one assumption is the
   shape §10.75 of the root CLAUDE.md is about; the fix is the same as it was
   for the marquees — one component, callers stay dumb.

   The format string comes from `shop.money_format`, published by theme.liquid
   as a JSON block (§4: no inline <script> in sections, pass data via JSON).
   So this file has NO opinion about currency at all: point it at a USD store
   and it prints dollars, point it at the India store and it prints rupees.
   That is deliberate — the two trees stay structurally portable (§0.5 rule 4).

   Shopify's placeholder set is implemented in full rather than just the two
   USD uses. It is a dozen lines, and the alternative is a formatter that
   silently prints "{{amount_with_comma_separator}}" as literal text the day
   someone changes a currency setting.

     amount                                    1,134.65
     amount_no_decimals                        1,135
     amount_with_comma_separator               1.134,65
     amount_no_decimals_with_comma_separator   1.135
     amount_with_apostrophe_separator          1'134.65
     amount_with_space_separator               1 134,65
     amount_no_decimals_with_space_separator   1 135

   API — both units, because the call sites genuinely have both:
     KitstersMoney.format(cents)     minor units, the Shopify convention
     KitstersMoney.formatMajor(n)    major units, e.g. Storefront API's
                                     `variant.price.amount` decimal string

   Callers must not divide by 100 themselves and must not add a symbol.
   ========================================================================== */
(function (w) {
  'use strict';

  var DEFAULT_FORMAT = '${{amount}}';

  var cfg = {};
  try {
    var el = document.getElementById('kitsters-money-config');
    if (el && el.textContent) cfg = JSON.parse(el.textContent) || {};
  } catch (e) { /* a malformed block must not take the page down */ }

  /* The fallback is USD because this is the US theme. It applies only when the
     JSON block is missing entirely — a real misconfiguration — so it is a
     safety net, never the normal path. */
  var FORMAT = (typeof cfg.format === 'string' && cfg.format) || DEFAULT_FORMAT;

  /* Group the integer part by threes. Written by hand rather than with
     toLocaleString because the separator is dictated by the FORMAT
     placeholder, not by the visitor's locale — a US shop must print "1,135"
     to a German visitor, not "1.135". */
  function group(intStr, sep) {
    return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }

  /* Takes an already-positive magnitude: the sign is handled once, around the
     whole formatted string, by formatMajor. Putting it here instead produces
     "$-12.99" — which is what the first run of tests/money.test.js reported,
     against Shopify's own `money` filter, which gives "-$12.99". */
  function render(units, decimals, thousandsSep, decimalSep) {
    var n = Number(units);
    if (!isFinite(n)) n = 0;

    var fixed = decimals ? n.toFixed(2) : String(Math.round(n));
    var parts = fixed.split('.');
    var out = group(parts[0], thousandsSep);
    if (decimals) out += decimalSep + parts[1];
    return out;
  }

  var PLACEHOLDERS = {
    amount:                                  [true,  ',', '.'],
    amount_no_decimals:                      [false, ',', '.'],
    amount_with_comma_separator:             [true,  '.', ','],
    amount_no_decimals_with_comma_separator: [false, '.', ','],
    amount_with_apostrophe_separator:        [true,  "'", '.'],
    amount_with_space_separator:             [true,  ' ', ','],
    amount_no_decimals_with_space_separator: [false, ' ', ',']
  };

  function formatMajor(units) {
    var n = Number(units);
    if (!isFinite(n)) n = 0;
    /* The sign belongs OUTSIDE the currency affix — "-$12.99", not "$-12.99" —
       which is what Shopify's `money` filter produces and what a shopper reads
       as a deduction rather than a typo. */
    var sign = n < 0 ? '-' : '';
    var magnitude = Math.abs(n);

    return sign + String(FORMAT).replace(/\{\{\s*(\w+)\s*\}\}/g, function (whole, key) {
      var spec = PLACEHOLDERS[key];
      /* An unknown placeholder is left verbatim rather than blanked: a visible
         "{{amount_foo}}" is a bug someone reports, an empty string is a price
         that silently disappears (§10.66c — wrong data that looks deliberate
         is worse than data that is visibly missing). */
      if (!spec) return whole;
      return render(magnitude, spec[0], spec[1], spec[2]);
    });
  }

  function format(cents) {
    var c = Number(cents);
    if (!isFinite(c)) c = 0;
    return formatMajor(c / 100);
  }

  w.KitstersMoney = {
    format: format,
    formatMajor: formatMajor,
    /* Exposed so a caller that must build its own string (a range label, an
       AI prompt line) can still ask what the shop's format is. */
    moneyFormat: function () { return FORMAT; }
  };
})(window);
